import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, RotateCcw, TriangleAlert } from 'lucide-react'
import type { Account } from '@limo/database'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'

interface ResetPreview {
  locationId: string
  resetTarget: string
  liveGrants: number
  users: number
  openPendingActions: number
  laCredential: boolean
  vaultRow: 'mine' | 'untagged' | 'foreign' | 'none'
  confirmCode: string
}

interface ResetResult {
  revokedFamilies: number
  revokedUsers: number
  cancelledPendingActions: number
  deletedAuthCodes: number
  laCredentialRemoved: boolean
  vaultRow: 'deleted' | 'foreign_kept' | 'none'
}

const ERROR_TEXT: Record<string, string> = {
  invalid_confirm_code: 'The preview expired. Close this and start the reset again.',
  peer_unauthorized:
    'The other Otto server rejected the portal secret. Set MILES_PEER_PARTNER_SECRET for the miles-portal function.',
  forbidden: 'Only platform admins can reset an account.',
}

// Non-2xx from the edge function: surface its error code so the admin gets
// a specific sentence instead of a generic failure.
async function invokeOttoPortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('miles-portal', { body })
  if (error) {
    const context = (error as { context?: Response }).context
    const detail = context ? await context.json().catch(() => null) : null
    throw new Error(detail?.error ?? 'unavailable')
  }
  return data as T
}

function vaultRowText(state: ResetPreview['vaultRow']): string {
  switch (state) {
    case 'mine':
    case 'untagged':
      return 'Will be removed'
    case 'foreign':
      return 'Belongs to the other Otto server — left in place'
    default:
      return 'None stored'
  }
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

// Admin-only "fresh slate" for one operator account: revokes everyone's
// Claude access, clears open approvals and stored credentials, and removes
// the GoHighLevel token row so the marketplace app can be uninstalled and
// re-installed cleanly. Two-step: preview first, then the admin types the
// location id to confirm. The edge function enforces the admin role.
export function OttoResetCard({ adminAccount }: { adminAccount: Account }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [preview, setPreview] = useState<ResetPreview | null>(null)
  const [typed, setTyped] = useState('')

  const close = () => {
    setPreview(null)
    setTyped('')
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['otto-status', adminAccount.id] })
    queryClient.invalidateQueries({ queryKey: ['otto-location-users', adminAccount.id] })
    queryClient.invalidateQueries({ queryKey: ['otto-la-status', adminAccount.id] })
  }

  const previewMutation = useMutation({
    mutationFn: () => invokeOttoPortal<ResetPreview>({ action: 'reset_preview', accountId: adminAccount.id }),
    onSuccess: (result) => setPreview(result),
    onError: (error: Error) =>
      toast({
        title: 'Could not prepare the reset',
        description: ERROR_TEXT[error.message] ?? 'Otto is temporarily unavailable. Please try again in a minute.',
        variant: 'destructive',
      }),
  })

  const resetMutation = useMutation({
    mutationFn: (confirmCode: string) =>
      invokeOttoPortal<ResetResult>({ action: 'reset', accountId: adminAccount.id, confirmCode }),
    onSuccess: (result) => {
      toast({
        title: 'Account reset',
        description:
          `Revoked ${result.revokedFamilies} Claude connection(s) from ${result.revokedUsers} user(s), ` +
          `cancelled ${result.cancelledPendingActions} pending action(s). ` +
          `Token row: ${result.vaultRow.replace('_', ' ')}. ` +
          'Next: uninstall the old Otto app on this sub-account in GoHighLevel, install the new one, then have each user reconnect.',
      })
      invalidate()
      close()
    },
    onError: (error: Error) =>
      toast({
        title: 'Reset failed',
        description: ERROR_TEXT[error.message] ?? 'Otto is temporarily unavailable. Nothing was changed.',
        variant: 'destructive',
      }),
  })

  const confirmed = preview !== null && typed.trim() === preview.locationId
  const busy = previewMutation.isPending || resetMutation.isPending

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TriangleAlert className="h-5 w-5 text-destructive" />
          Reset this account
        </CardTitle>
        <CardDescription>
          Admin only. Disconnects every team member&apos;s Claude, clears pending approvals and stored
          credentials, and removes the GoHighLevel token so the Otto app can be uninstalled and re-installed
          from scratch. Agency-level tokens and other accounts are never touched.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="destructive" disabled={busy} onClick={() => previewMutation.mutate()}>
          {previewMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="mr-2 h-4 w-4" />
          )}
          Reset {adminAccount.name}…
        </Button>

        <Dialog open={preview !== null} onOpenChange={(open) => !open && !resetMutation.isPending && close()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset {adminAccount.name}?</DialogTitle>
              <DialogDescription>
                This is what will happen on the Otto server at{' '}
                <span className="font-mono text-xs">{preview?.resetTarget}</span>. It cannot be undone; users
                will need to reconnect Claude afterwards.
              </DialogDescription>
            </DialogHeader>
            {preview && (
              <div className="divide-y rounded-md border px-3">
                <PreviewRow
                  label="Claude connections to revoke"
                  value={`${preview.liveGrants} (${preview.users} user${preview.users === 1 ? '' : 's'})`}
                />
                <PreviewRow label="Pending write approvals to cancel" value={String(preview.openPendingActions)} />
                <PreviewRow
                  label="Stored LimoAnywhere login"
                  value={preview.laCredential ? 'Will be removed' : 'None stored'}
                />
                <PreviewRow label="GoHighLevel token row" value={vaultRowText(preview.vaultRow)} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="otto-reset-confirm">
                Type the location ID <span className="font-mono">{preview?.locationId}</span> to confirm
              </Label>
              <Input
                id="otto-reset-confirm"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={resetMutation.isPending}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={resetMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={!confirmed || resetMutation.isPending}
                onClick={() => preview && resetMutation.mutate(preview.confirmCode)}
              >
                {resetMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reset account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
